import { FC, useCallback, useContext, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Body, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { ExperienceContext } from "@/App/context";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { useIsUnifiedUser } from "@/App/hooks";
import { MemberCardBack, MemberCardFront, PrintableWrapper, ScreenReader, ThemedButton, UnifiedInformation } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";

export const PrintCardPage: FC = () => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { isUnifiedUser } = useIsUnifiedUser();
  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  const idCardDescription = useMemo(() => {
    if (isUnifiedUser) {
      return translate("PRINT_CARD_PAGE.idCardDescriptionisUnifiedUserTrue");
    } else {
      return translate("PRINT_CARD_PAGE.idCardDescriptionisUnifiedUserFalse");
    }
  }, [isUnifiedUser]);

  const findProviderDescription = useMemo(() => {
    if (isUnifiedUser) {
      return translate("PRINT_CARD_PAGE.findProviderDescriptionisUnifiedUserTrue");
    } else {
      return translate("PRINT_CARD_PAGE.findProviderDescriptionisUnifiedUserFalse");
    }
  }, [isUnifiedUser]);

  return (
    <PrintableWrapper ref={printRef}>
      <Grid testId="print-card-container">
        <Grid.Span columns={12}>
          <Shelf>
            <Shelf.Spacer />
            <ThemedButton testId="print-card" appearance="text" onClick={onPrint}>
              <ScreenReader>{translate("PRINT_CARD_PAGE.printCard")}</ScreenReader>
              <Icon name="print" />
            </ThemedButton>
          </Shelf>
        </Grid.Span>
        <Grid.Span columns={12}>
          <Stack>
            <Subtitle>{translate("PRINT_CARD_PAGE.yourIdCard")}</Subtitle>
            <Body>{idCardDescription}</Body>

            <Subtitle>{translate("PRINT_CARD_PAGE.find")} {isUnifiedUser ? translate("PRINT_CARD_PAGE.aProviderOr") : ""} {translate("PRINT_CARD_PAGE.aPharmacy")}</Subtitle>
            <Body>{findProviderDescription}</Body>

            <Subtitle>{translate("PRINT_CARD_PAGE.customerSupport")}</Subtitle>
            <Body testId="customer-care-content">{translate("PRINT_CARD_PAGE.customerSupportCare")} {customerSupportNumber}.</Body>
          </Stack>
        </Grid.Span>

        <Grid.Span columns={6}>
          <Stack alignment="center" testId="cards">
            <MemberCardFront />
            <MemberCardBack />
          </Stack>
        </Grid.Span>

        <Grid.Span columns={1} />
        <Grid.Span columns={4}>{isUnifiedUser && <UnifiedInformation />}</Grid.Span>
        <Grid.Span columns={1} />
      </Grid>
    </PrintableWrapper>
  );
};
